import {FC} from 'react'
import {
    Modal,
    ModalBody,
    ModalFooter,
    ModalHeader,
    ModalOverlay,
    ModalTitle,
    ModalXCloseButton
} from '@/components/ui/modal-atoms/ModalAtoms'
import {Button} from '@/components/ui/button/Button'
import {Text} from '@/components/ui/text/Text'
import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {Table} from '@/components/commons/table/Table'
import {TableBodyCell, TableBodyCellText} from '@/components/ui/table-atoms/TableAtoms'
import {EmptyState} from '@/components/commons/empty-state/EmptyState'
import {Tooltip} from '@/components/ui/tooltip/Tooltip'
import {TKey, useTranslation} from '@/translations/i18n'
import {formatDisplayDateTime} from '@/dayjs'
import {useMailBatchDetail} from '@/features/mailer/queries/useMailBatchDetail'
import {MailerBatchStatusBadge} from '@/features/mailer/components/mailer-batch-status-badge/MailerBatchStatusBadge'
import {MailLog, MailLogStatus, MailRecipientKind} from '@/features/mailer/types'
import {StyledErrorCell, StyledFilterChip, StyledFilterChips, StyledLogsContainer, StyledRecipientCell} from './style'

interface MailerBatchDetailsModalProps {
    batchId: string
    page: number
    limit: number
    status?: MailLogStatus
    onClose: () => void
    onChangePage: (page: number) => void
    onChangeStatus: (status: MailLogStatus | undefined) => void
}

const STATUS_FILTERS: Array<{value: MailLogStatus | undefined; labelKey: TKey}> = [
    {value: undefined, labelKey: 'mailer:batch_detail_filter_all'},
    {value: 'sent', labelKey: 'mailer:batch_detail_filter_sent'},
    {value: 'failed', labelKey: 'mailer:batch_detail_filter_failed'},
    {value: 'bounced', labelKey: 'mailer:batch_detail_filter_bounced'},
    {value: 'pending', labelKey: 'mailer:batch_detail_filter_pending'}
]

const RECIPIENT_KIND_LABEL: Record<MailRecipientKind, TKey> = {
    patient: 'mailer:recipient_kind_patient',
    consultant: 'mailer:recipient_kind_consultant',
    whitelist: 'mailer:recipient_kind_whitelist'
}

const COLUMN_IDS = {
    recipient: 'recipient',
    kind: 'kind',
    status: 'status',
    attempts: 'attempts',
    sentAt: 'sentAt',
    error: 'error'
} as const

type ColumnId = (typeof COLUMN_IDS)[keyof typeof COLUMN_IDS]

export const MailerBatchDetailsModal: FC<MailerBatchDetailsModalProps> = ({
    batchId,
    page,
    limit,
    status,
    onClose,
    onChangePage,
    onChangeStatus
}) => {
    const {t} = useTranslation()
    const detailQuery = useMailBatchDetail({batchId, page, limit, status})

    const headerSubject = detailQuery.data?.data[0]?.subject

    return (
        <>
            <ModalOverlay onClick={onClose} />
            <Modal width="1080px">
                <ModalHeader>
                    <Flexbox justify="space-between" align="center" fullWidth>
                        <Flexbox direction="column" gap={0.5}>
                            <ModalTitle>{t('mailer:batch_detail_title')}</ModalTitle>
                            {headerSubject && (
                                <Text size="textSm" palette="neutral" shade="600" title={headerSubject}>
                                    {headerSubject}
                                </Text>
                            )}
                        </Flexbox>
                        <ModalXCloseButton onClick={onClose} />
                    </Flexbox>
                </ModalHeader>
                <ModalBody>
                    <StyledFilterChips role="tablist" aria-label={t('mailer:batch_detail_filter_all')}>
                        {STATUS_FILTERS.map(filter => {
                            const isActive = filter.value === status
                            return (
                                <StyledFilterChip
                                    key={filter.value ?? 'all'}
                                    type="button"
                                    role="tab"
                                    aria-selected={isActive}
                                    $active={isActive}
                                    onClick={() => onChangeStatus(filter.value)}
                                >
                                    {t(filter.labelKey)}
                                </StyledFilterChip>
                            )
                        })}
                    </StyledFilterChips>

                    <StyledLogsContainer>
                        {detailQuery.isError ? (
                            <EmptyState
                                title={t('mailer:error_batch_not_found')}
                                subtitle={t('errors:default')}
                                variant="secondary"
                            />
                        ) : (
                            <Table<MailLog[], ColumnId>
                                emptyStateComponent={
                                    <EmptyState
                                        title={t('mailer:batch_detail_empty_title')}
                                        subtitle={t('mailer:batch_detail_empty_description')}
                                    />
                                }
                                pagination={{
                                    ...detailQuery.persistedPagination,
                                    currentPage: page,
                                    limit,
                                    onChangePage,
                                    onChangeLimit: () => undefined
                                }}
                                isLoading={detailQuery.isPending}
                                data={detailQuery.data?.data ?? []}
                                columns={[
                                    {
                                        id: COLUMN_IDS.recipient,
                                        header: () => t('mailer:batch_detail_column_recipient'),
                                        cell: ({dataItem, forwardedProps}) => (
                                            <TableBodyCell {...forwardedProps}>
                                                <StyledRecipientCell>
                                                    <TableBodyCellText
                                                        variant="dark"
                                                        title={dataItem.recipient}
                                                        className="email"
                                                    >
                                                        {dataItem.recipient}
                                                    </TableBodyCellText>
                                                </StyledRecipientCell>
                                            </TableBodyCell>
                                        ),
                                        minSize: () => 200,
                                        size: () => 240,
                                        maxSize: () => 320
                                    },
                                    {
                                        id: COLUMN_IDS.kind,
                                        header: () => t('mailer:batch_detail_column_kind'),
                                        cell: ({dataItem, forwardedProps}) => (
                                            <TableBodyCell {...forwardedProps}>
                                                <TableBodyCellText>
                                                    {t(RECIPIENT_KIND_LABEL[dataItem.recipientKind])}
                                                </TableBodyCellText>
                                            </TableBodyCell>
                                        ),
                                        size: () => 110
                                    },
                                    {
                                        id: COLUMN_IDS.status,
                                        header: () => t('mailer:batch_detail_column_status'),
                                        cell: ({dataItem, forwardedProps}) => (
                                            <TableBodyCell {...forwardedProps}>
                                                <MailerBatchStatusBadge status={dataItem.status} />
                                            </TableBodyCell>
                                        ),
                                        size: () => 110
                                    },
                                    {
                                        id: COLUMN_IDS.attempts,
                                        header: () => t('mailer:batch_detail_column_attempts'),
                                        cell: ({dataItem, forwardedProps}) => (
                                            <TableBodyCell {...forwardedProps}>
                                                <TableBodyCellText>{dataItem.attempts}</TableBodyCellText>
                                            </TableBodyCell>
                                        ),
                                        size: () => 90
                                    },
                                    {
                                        id: COLUMN_IDS.sentAt,
                                        header: () => t('mailer:batch_detail_column_sent_at'),
                                        cell: ({dataItem, forwardedProps}) => (
                                            <TableBodyCell {...forwardedProps}>
                                                {dataItem.sentAt ? (
                                                    <TableBodyCellText>
                                                        {formatDisplayDateTime(dataItem.sentAt)}
                                                    </TableBodyCellText>
                                                ) : (
                                                    <TableBodyCellText variant="light">—</TableBodyCellText>
                                                )}
                                            </TableBodyCell>
                                        ),
                                        size: () => 150
                                    },
                                    {
                                        id: COLUMN_IDS.error,
                                        header: () => t('mailer:batch_detail_column_error'),
                                        cell: ({dataItem, forwardedProps}) => (
                                            <TableBodyCell {...forwardedProps}>
                                                {dataItem.error ? (
                                                    <Tooltip content={dataItem.error} variant="light" maxWidth="320px">
                                                        <StyledErrorCell>{dataItem.error}</StyledErrorCell>
                                                    </Tooltip>
                                                ) : (
                                                    <TableBodyCellText variant="light">—</TableBodyCellText>
                                                )}
                                            </TableBodyCell>
                                        ),
                                        size: () => 260
                                    }
                                ]}
                            />
                        )}
                    </StyledLogsContainer>
                </ModalBody>
                <ModalFooter>
                    <Button type="button" variant="primaryNeutral" onClick={onClose}>
                        <Text size="textMd">{t('commons:close')}</Text>
                    </Button>
                </ModalFooter>
            </Modal>
        </>
    )
}
